import { supabase } from '../lib/supabase';
import { urgentSMSService } from './urgentSMSService';

export const escalationService = {
  /**
   * Get all escalation rules for a company
   */
  async getEscalationRules(companyId = null) {
    try {
      let query = supabase
        ?.from('escalation_rules')
        ?.select(`
          *,
          created_by_user:user_profiles!escalation_rules_created_by_fkey(full_name, email)
        `)
        ?.order('priority', { ascending: false });

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(rule => ({
        id: rule?.id,
        companyId: rule?.company_id,
        createdBy: rule?.created_by,
        createdByUser: rule?.created_by_user,
        ruleName: rule?.rule_name,
        description: rule?.description,
        status: rule?.status,
        alertTypes: rule?.alert_types || [],
        severityLevels: rule?.severity_levels || [],
        departments: rule?.departments || [],
        responseTimeoutMinutes: rule?.response_timeout_minutes,
        checkIntervalMinutes: rule?.check_interval_minutes,
        escalationActions: rule?.escalation_actions || [],
        escalateToRole: rule?.escalate_to_role,
        notifyViaSms: rule?.notify_via_sms,
        notifyViaEmail: rule?.notify_via_email,
        priority: rule?.priority,
        isDefault: rule?.is_default,
        metadata: rule?.metadata || {},
        createdAt: rule?.created_at,
        updatedAt: rule?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching escalation rules:', error);
      throw error;
    }
  },

  /**
   * Create new escalation rule
   */
  async createEscalationRule(ruleData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: employeeData } = await supabase
        ?.from('company_employees')
        ?.select('company_id')
        ?.eq('user_id', user?.id)
        ?.single();

      if (!employeeData) throw new Error('User not associated with a company');

      const { data, error } = await supabase
        ?.from('escalation_rules')
        ?.insert({
          company_id: employeeData?.company_id,
          created_by: user?.id,
          rule_name: ruleData?.ruleName,
          description: ruleData?.description,
          status: ruleData?.status || 'active',
          alert_types: ruleData?.alertTypes || [],
          severity_levels: ruleData?.severityLevels || [],
          departments: ruleData?.departments || [],
          response_timeout_minutes: ruleData?.responseTimeoutMinutes || 60,
          check_interval_minutes: ruleData?.checkIntervalMinutes || 15,
          escalation_actions: ruleData?.escalationActions || [],
          escalate_to_role: ruleData?.escalateToRole || 'supervisor',
          notify_via_sms: ruleData?.notifyViaSms !== false,
          notify_via_email: ruleData?.notifyViaEmail !== false,
          priority: ruleData?.priority || 1,
          metadata: ruleData?.metadata || {}
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating escalation rule:', error);
      throw error;
    }
  },

  /**
   * Update escalation rule
   */
  async updateEscalationRule(ruleId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('escalation_rules')
        ?.update({
          rule_name: updates?.ruleName,
          description: updates?.description,
          status: updates?.status,
          alert_types: updates?.alertTypes,
          severity_levels: updates?.severityLevels,
          departments: updates?.departments,
          response_timeout_minutes: updates?.responseTimeoutMinutes,
          check_interval_minutes: updates?.checkIntervalMinutes,
          escalation_actions: updates?.escalationActions,
          escalate_to_role: updates?.escalateToRole,
          notify_via_sms: updates?.notifyViaSms,
          notify_via_email: updates?.notifyViaEmail,
          priority: updates?.priority,
          metadata: updates?.metadata,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', ruleId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating escalation rule:', error);
      throw error;
    }
  },

  /**
   * Delete escalation rule
   */
  async deleteEscalationRule(ruleId) {
    try {
      const { error } = await supabase
        ?.from('escalation_rules')
        ?.delete()
        ?.eq('id', ruleId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting escalation rule:', error);
      throw error;
    }
  },

  /**
   * Track alert assignment for escalation monitoring
   */
  async trackAlertAssignment(alertData) {
    try {
      const { data, error } = await supabase
        ?.from('alert_response_tracking')
        ?.insert({
          alert_id: alertData?.alertId,
          alert_type: alertData?.alertType,
          alert_severity: alertData?.severity,
          assigned_to: alertData?.assignedTo,
          assigned_by: alertData?.assignedBy,
          company_id: alertData?.companyId,
          metadata: alertData?.metadata || {}
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error tracking alert assignment:', error);
      throw error;
    }
  },

  /**
   * Mark alert as acknowledged
   */
  async acknowledgeAlert(trackingId, userId) {
    try {
      const { data, error } = await supabase
        ?.from('alert_response_tracking')
        ?.update({
          acknowledged_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', trackingId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      throw error;
    }
  },

  /**
   * Check for alerts that need escalation
   */
  async checkAlertsForEscalation(companyId = null) {
    try {
      // Get unacknowledged alert trackings
      let query = supabase
        ?.from('alert_response_tracking')
        ?.select('*')
        ?.eq('is_escalated', false)
        ?.is('acknowledged_at', null);

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      const { data: trackings, error: trackingError } = await query;
      if (trackingError) throw trackingError;

      const escalationsNeeded = [];

      for (const tracking of trackings || []) {
        // Calculate time elapsed
        const assignedAt = new Date(tracking?.assigned_at);
        const now = new Date();
        const minutesElapsed = Math.floor((now - assignedAt) / (1000 * 60));

        // Get matching escalation rule
        const { data: rules, error: rulesError } = await supabase
          ?.from('escalation_rules')
          ?.select('*')
          ?.eq('company_id', tracking?.company_id)
          ?.eq('status', 'active')
          ?.order('priority', { ascending: false });

        if (rulesError) {
          console.error('Error fetching escalation rules:', rulesError);
          continue;
        }

        // Find matching rule
        const matchingRule = rules?.find(rule => {
          const alertTypeMatch = !rule?.alert_types?.length || rule?.alert_types?.includes(tracking?.alert_type);
          const severityMatch = !rule?.severity_levels?.length || rule?.severity_levels?.includes(tracking?.alert_severity);
          const timeoutExceeded = minutesElapsed >= rule?.response_timeout_minutes;

          return alertTypeMatch && severityMatch && timeoutExceeded;
        });

        if (matchingRule) {
          escalationsNeeded?.push({
            tracking,
            rule: matchingRule,
            minutesElapsed
          });
        }
      }

      return escalationsNeeded;
    } catch (error) {
      console.error('Error checking alerts for escalation:', error);
      throw error;
    }
  },

  /**
   * Trigger escalation for an alert
   */
  async triggerEscalation(trackingId, ruleId) {
    try {
      // Get tracking and rule details
      const { data: tracking, error: trackingError } = await supabase
        ?.from('alert_response_tracking')
        ?.select('*')
        ?.eq('id', trackingId)
        ?.single();

      if (trackingError) throw trackingError;

      const { data: rule, error: ruleError } = await supabase
        ?.from('escalation_rules')
        ?.select('*')
        ?.eq('id', ruleId)
        ?.single();

      if (ruleError) throw ruleError;

      // Get supervisor or role-based assignee
      let escalatedTo = null;

      if (rule?.escalate_to_role === 'supervisor') {
        // Get supervisor from company_employees
        const { data: supervisor } = await supabase
          ?.from('company_employees')
          ?.select('supervisor_id')
          ?.eq('user_id', tracking?.assigned_to)
          ?.eq('status', 'active')
          ?.single();

        escalatedTo = supervisor?.supervisor_id;
      } else {
        // Get user by role
        const { data: roleUsers } = await supabase
          ?.from('user_profiles')
          ?.select('id')
          ?.eq('role', rule?.escalate_to_role)
          ?.limit(1)
          ?.single();

        escalatedTo = roleUsers?.id;
      }

      if (!escalatedTo) {
        throw new Error('No escalation target found');
      }

      // Calculate response time
      const assignedAt = new Date(tracking?.assigned_at);
      const now = new Date();
      const minutesElapsed = Math.floor((now - assignedAt) / (1000 * 60));

      // Create escalation history
      const { data: escalationHistory, error: historyError } = await supabase
        ?.from('escalation_history')
        ?.insert({
          alert_response_tracking_id: trackingId,
          escalation_rule_id: ruleId,
          original_assignee: tracking?.assigned_to,
          original_assigned_at: tracking?.assigned_at,
          escalated_to: escalatedTo,
          escalation_reason: `No response within ${rule?.response_timeout_minutes} minutes`,
          timeout_minutes: rule?.response_timeout_minutes,
          actual_response_time_minutes: minutesElapsed,
          company_id: tracking?.company_id,
          escalation_status: 'triggered'
        })
        ?.select()
        ?.single();

      if (historyError) throw historyError;

      // Update tracking record
      await supabase
        ?.from('alert_response_tracking')
        ?.update({
          is_escalated: true,
          escalated_at: new Date()?.toISOString(),
          escalation_reason: `Escalated to supervisor after ${minutesElapsed} minutes`,
          escalation_rule_id: ruleId,
          assigned_to: escalatedTo,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', trackingId);

      // Send notifications
      if (rule?.notify_via_sms) {
        await this.sendEscalationSMS(escalationHistory?.id, escalatedTo, tracking, rule);
      }

      return escalationHistory;
    } catch (error) {
      console.error('Error triggering escalation:', error);
      throw error;
    }
  },

  /**
   * Send escalation SMS notification
   */
  async sendEscalationSMS(escalationHistoryId, recipientUserId, tracking, rule) {
    try {
      const message = `⚠️ ESCALATED ALERT: ${tracking?.alert_type?.toUpperCase()} (${tracking?.alert_severity?.toUpperCase()})\n\nAlert ID: ${tracking?.alert_id}\nOriginal assignee did not respond within ${rule?.response_timeout_minutes} minutes.\n\nPlease review and take action immediately.`;

      await urgentSMSService?.sendUrgentAlert({
        alertType: 'escalation',
        severity: tracking?.alert_severity || 'high',
        title: 'Alert Escalated',
        message,
        recipientUserIds: [recipientUserId]
      });

      // Update escalation history
      await supabase
        ?.from('escalation_history')
        ?.update({
          sms_sent: true,
          sms_sent_at: new Date()?.toISOString()
        })
        ?.eq('id', escalationHistoryId);

      return { success: true };
    } catch (error) {
      console.error('Error sending escalation SMS:', error);
      throw error;
    }
  },

  /**
   * Get escalation history
   */
  async getEscalationHistory(filters = {}) {
    try {
      let query = supabase
        ?.from('escalation_history')
        ?.select(`
          *,
          original_assignee_user:user_profiles!escalation_history_original_assignee_fkey(full_name, email),
          escalated_to_user:user_profiles!escalation_history_escalated_to_fkey(full_name, email),
          escalation_rule:escalation_rules(rule_name, response_timeout_minutes)
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.companyId) {
        query = query?.eq('company_id', filters?.companyId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(history => ({
        id: history?.id,
        alertResponseTrackingId: history?.alert_response_tracking_id,
        escalationRuleId: history?.escalation_rule_id,
        escalationRule: history?.escalation_rule,
        originalAssignee: history?.original_assignee,
        originalAssigneeUser: history?.original_assignee_user,
        originalAssignedAt: history?.original_assigned_at,
        escalatedTo: history?.escalated_to,
        escalatedToUser: history?.escalated_to_user,
        escalatedAt: history?.escalated_at,
        escalationReason: history?.escalation_reason,
        escalationActionsTaken: history?.escalation_actions_taken || [],
        timeoutMinutes: history?.timeout_minutes,
        actualResponseTimeMinutes: history?.actual_response_time_minutes,
        escalationStatus: history?.escalation_status,
        resolvedAt: history?.resolved_at,
        resolutionNotes: history?.resolution_notes,
        smsSent: history?.sms_sent,
        smsSentAt: history?.sms_sent_at,
        emailSent: history?.email_sent,
        emailSentAt: history?.email_sent_at,
        companyId: history?.company_id,
        metadata: history?.metadata || {},
        createdAt: history?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching escalation history:', error);
      throw error;
    }
  },

  /**
   * Get escalation statistics
   */
  async getEscalationStats(companyId, dateRange = {}) {
    try {
      let query = supabase
        ?.from('escalation_history')
        ?.select('*');

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      if (dateRange?.from) {
        query = query?.gte('created_at', dateRange?.from);
      }

      if (dateRange?.to) {
        query = query?.lte('created_at', dateRange?.to);
      }

      const { data, error } = await query;
      if (error) throw error;

      const totalEscalations = data?.length || 0;
      const resolvedEscalations = data?.filter(e => e?.escalation_status === 'completed')?.length || 0;
      const pendingEscalations = data?.filter(e => e?.escalation_status === 'triggered')?.length || 0;
      const avgResponseTime = data?.reduce((sum, e) => sum + (e?.actual_response_time_minutes || 0), 0) / totalEscalations || 0;

      return {
        totalEscalations,
        resolvedEscalations,
        pendingEscalations,
        avgResponseTime: Math.round(avgResponseTime),
        resolutionRate: totalEscalations > 0 ? Math.round((resolvedEscalations / totalEscalations) * 100) : 0
      };
    } catch (error) {
      console.error('Error fetching escalation stats:', error);
      throw error;
    }
  }
};